import type { AccountGroupState } from './account-group-selectors';
import { STARTING, SUCCESS, FAILURE } from '../action/util/action-name-creators';
import {
  Action,
  deleteResourceByParam,
  setStateDirtyByParam,
  updateResourceByParam,
} from '../reducers/utils/state-manager';

import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: AccountGroupState = {
  accountGroupsById: {},
  accountGroupsStateById: {},
  accountGroupsStateByUsername: {},
};

// Managing account groups is only set up for TE admins right now.
// When no username is provided, we know that the user who tried to take these
// actions is the TE admin user. We opted to just store it under this word admin
// instead of undefined because it makes it a little more clear when looking
// through the redux state
// It is not stored by the admin's username because not providing a username is
// how we know you're making an admin request
const ADMIN_USERNAME = 'admin';

export const accountGroupReducer = (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('CREATE_ACCOUNT_GROUP'):
    case STARTING('LOAD_ACCOUNT_GROUPS'): {
      const { username } = action.payload.args;
      return updateResourceByParam(
        state,
        'accountGroups',
        'username',
        username ?? ADMIN_USERNAME,
        true,
      );
    }
    case SUCCESS('LOAD_ACCOUNT_GROUPS'): {
      const {
        args: { username },
        data,
      } = action.payload;
      return updateResourceByParam(
        state,
        'accountGroups',
        'username',
        username ?? ADMIN_USERNAME,
        false,
        convertArrayToMap(data, ({ accountGroupId }: AccountGroup) => String(accountGroupId)),
      );
    }
    case FAILURE('LOAD_ACCOUNT_GROUPS'): {
      const { username } = action.payload.args;
      return updateResourceByParam(
        state,
        'accountGroups',
        'username',
        username ?? ADMIN_USERNAME,
        false,
      );
    }
    case SUCCESS('CREATE_ACCOUNT_GROUP'): {
      const { data }: { data: AccountGroup } = action.payload;
      // This action is only possible for admins to do so we'll always add the
      // new group to state instead of refetching from the server
      const newAccountGroups = Object.assign(state.accountGroupsById, {
        [data.accountGroupId]: data,
      });
      return updateResourceByParam(
        state,
        'accountGroups',
        'username',
        ADMIN_USERNAME,
        false,
        newAccountGroups,
      );
    }
    case STARTING('CREATE_ACCOUNT_GROUP_USER'):
    case STARTING('REMOVE_ACCOUNT_FROM_GROUP_ACCOUNT'):
    case STARTING('ADD_ACCOUNT_TO_GROUP_ACCOUNT'):
    case STARTING('DELETE_ACCOUNT_GROUP'):
    case STARTING('UPDATE_ACCOUNT_GROUP'): {
      const { accountGroupId } = action.payload.args;
      return updateResourceByParam(state, 'accountGroups', 'id', accountGroupId, true);
    }
    case SUCCESS('REMOVE_ACCOUNT_FROM_GROUP_ACCOUNT'):
    case SUCCESS('ADD_ACCOUNT_TO_GROUP_ACCOUNT'):
    case SUCCESS('UPDATE_ACCOUNT_GROUP'): {
      const {
        data,
        args: { accountGroupId },
      }: { data: AccountGroup; args: { accountGroupId: string } } = action.payload;
      return updateResourceByParam(state, 'accountGroups', 'id', accountGroupId, false, {
        [accountGroupId]: data,
      });
    }
    case SUCCESS('DELETE_ACCOUNT_GROUP'): {
      const { accountGroupId } = action.payload.args;
      return deleteResourceByParam(state, 'accountGroups', 'id', accountGroupId, accountGroupId);
    }
    case SUCCESS('CREATE_ACCOUNT_GROUP_USER'): {
      const {
        data,
        args: { accountGroupId },
      } = action.payload;
      // We're mutating the state directly here but we need to also update the
      // loading state so we pass our mutated accountGroup to the update function
      // and it handles updating the loading to success and adds in our mutated
      // state
      state.accountGroupsById[accountGroupId].Users.push(data);
      return updateResourceByParam(state, 'accountGroups', 'id', accountGroupId, false, {
        [accountGroupId]: state.accountGroupsById[accountGroupId],
      });
    }
    case SUCCESS('UPDATE_USER'): {
      const { data }: { data: User } = action.payload;
      const { accountGroupId } = data;
      if (!accountGroupId) {
        return state;
      }
      state.accountGroupsById[accountGroupId].Users = state.accountGroupsById[
        accountGroupId
      ].Users.map((u) => (u.username === data.username ? data : u));
      return updateResourceByParam(state, 'accountGroups', 'id', String(accountGroupId), false, {
        [accountGroupId]: state.accountGroupsById[accountGroupId],
      });
    }
    case STARTING('DISABLE_USER'):
    case STARTING('ENABLE_USER'): {
      const { reducerData } = action.payload.args;
      if (!reducerData?.accountGroupId) {
        return state;
      }
      return updateResourceByParam(state, 'accountGroups', 'id', reducerData.accountGroupId, true);
    }
    case SUCCESS('DISABLE_USER'):
    case SUCCESS('ENABLE_USER'): {
      const { data } = action.payload;
      const { reducerData } = action.payload.args;
      if (!reducerData?.accountGroupId) {
        return state;
      }
      const { accountGroupId } = reducerData;
      state.accountGroupsById[accountGroupId].Users = state.accountGroupsById[
        accountGroupId
      ].Users.map((u) => (u.username === data.username ? data : u));
      return updateResourceByParam(state, 'accountGroups', 'id', accountGroupId, false, {
        [accountGroupId]: state.accountGroupsById[accountGroupId],
      });
    }
    case SUCCESS('MOVE_USER'): {
      const { user: updatedUser }: { user: User } = action.payload.data;
      const { user }: { user: User } = action.payload.args;
      let newState = { ...state };

      if (user.accountGroupId) {
        newState = setStateDirtyByParam(
          newState,
          'accountGroups',
          'id',
          user.accountGroupId.toString(),
        );
      }
      if (updatedUser.accountGroupId) {
        newState = setStateDirtyByParam(
          newState,
          'accountGroups',
          'id',
          updatedUser.accountGroupId.toString(),
        );
      }

      return setStateDirtyByParam(newState, 'accountGroups', 'username', ADMIN_USERNAME);
    }
    default: {
      return state;
    }
  }
};
